import {
    AddToWishlistResponse,
    authorizedSyliusFetch,
    CreateWishlistResponse,
    DeleteFromWishlistResponse,
    GetWishlistResponse,
    syliusCustomerFetch,
    syliusFetch,
} from '../../../entities/@api/Sylius';
import { Wishlist, wishlistToken } from '../../../entities/@shop/Wishlist/Wishlist';
import { transformToWishlist } from '../../../entities/@shop/Wishlist/WishlistTransformers';
import { isFetchResultSuccessful } from '../../../entities/FetchResult/FetchResult';
import { ReduxFetchAction } from '../../defaults';
import {
    setError,
    setIsLoading,
    setIsSuccessfullyAdded,
    setIsSuccessfullyRemoved,
    setWishlist,
} from './wishlistSlice';

export const addProductVariantToWishlist: ReduxFetchAction<string> = productVariantId => async (dispatch, getState) => {
    dispatch(setIsSuccessfullyAdded(false));
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { wishlistSlice, customerSlice } = getState();
        const { customer } = customerSlice;
        const { wishlist } = wishlistSlice;

        const body = { productVariantId: Number(productVariantId) };

        const addEndpoint = `${wishlist.id}/variant`;
        const addToWishlistResponse = await syliusCustomerFetch<AddToWishlistResponse>(`/shop/wishlists/${addEndpoint}`, {
            customer,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/merge-patch+json' },
            body: JSON.stringify(body),
        });

        if (!isFetchResultSuccessful(addToWishlistResponse)) {
            dispatch(setError(addToWishlistResponse.error));
            return;
        }

        const wishlistResource = addToWishlistResponse.data;
        const transformedWishlist = transformToWishlist(wishlistResource);

        dispatch(setWishlist(transformedWishlist));
        dispatch(setIsSuccessfullyAdded(true));
    } catch (error) {
        console.error('[addProductVariantToWishList]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const removeProductVariantFromWishlist: ReduxFetchAction<string> = productVariantId => async (dispatch, getState) => {
    dispatch(setIsSuccessfullyRemoved(false));
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { wishlistSlice, customerSlice } = getState();
        const { customer } = customerSlice;
        const { wishlist } = wishlistSlice;

        const removeEndpoint = `${wishlist.id}/productVariants/${productVariantId}`;
        const removeFromWishlistResponse = await syliusCustomerFetch<DeleteFromWishlistResponse>(`/shop/wishlists/${removeEndpoint}`, {
            customer,
            method: 'DELETE',
        });

        if (!isFetchResultSuccessful(removeFromWishlistResponse)) {
            dispatch(setError(removeFromWishlistResponse.error));
            return;
        }

        const variantIds = wishlist.variantIds.filter(item => item !== productVariantId);

        const transformedWishlist: Wishlist = {
            ...wishlist,
            variantIds,
        };

        dispatch(setWishlist(transformedWishlist));
        dispatch(setIsSuccessfullyRemoved(true));
    } catch (error) {
        console.error('[removeProductVariantFromWishlist]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

const createAndSetWishlist: ReduxFetchAction = () => async dispatch => {
    const createWishlistResponse = await syliusFetch<CreateWishlistResponse>('/shop/wishlists', {
        method: 'POST',
        body: JSON.stringify({ localeCode: 'nl' }),
    });

    if (!isFetchResultSuccessful(createWishlistResponse)) {
        dispatch(setError(createWishlistResponse.error));
        return;
    }

    const createWishlistResource = createWishlistResponse.data;
    const { token = '' } = createWishlistResource;

    localStorage.setItem(wishlistToken, JSON.stringify(token));

    const wishlist = transformToWishlist(createWishlistResource);

    dispatch(setWishlist(wishlist));
};

const getAndSetWishlist: ReduxFetchAction<string> = storedWishlistToken => async dispatch => {
    const wishlistTokenValue = JSON.parse(storedWishlistToken);

    const wishlistResponse = await syliusFetch<GetWishlistResponse>(`/shop/wishlists/${wishlistTokenValue}`);

    if (!isFetchResultSuccessful(wishlistResponse)) {
        dispatch(setError(wishlistResponse.error));

        await dispatch(createAndSetWishlist());
        return;
    }

    const wishlistResource = wishlistResponse.data;
    const wishlist = transformToWishlist(wishlistResource);

    dispatch(setWishlist(wishlist));
};

const handleCustomerWishlist: ReduxFetchAction = () => async (dispatch, getState) => {
    try {
        const { wishlistSlice } = getState();
        const { wishlist } = wishlistSlice;

        const productVariantIdsInLocalStorage = localStorage.getItem(wishlistToken)
            ? wishlist.variantIds
            : [];

        localStorage.removeItem(wishlistToken);

        const wishlistResponse = await authorizedSyliusFetch<CreateWishlistResponse>('/shop/wishlists', {
            method: 'POST',
            body: JSON.stringify({ localeCode: 'nl' }),
        });

        if (!isFetchResultSuccessful(wishlistResponse)) {
            dispatch(setError(wishlistResponse.error));
            return;
        }

        const wishlistResource = wishlistResponse.data;
        const transformedWishlist = transformToWishlist(wishlistResource);

        dispatch(setWishlist(transformedWishlist));

        if (productVariantIdsInLocalStorage.length) {
            // Merge local wishlist items in wishlist of customer
            productVariantIdsInLocalStorage.map(async productVariantId => dispatch(addProductVariantToWishlist(productVariantId)));
        }
    } catch (error) {
        console.error('[handleCustomerWishlist]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

const handleGuestWishlist: ReduxFetchAction = () => async dispatch => {
    const storedWishlistToken = localStorage.getItem(wishlistToken);

    if (!storedWishlistToken) {
        await dispatch(createAndSetWishlist());
        return;
    }

    await dispatch(getAndSetWishlist(storedWishlistToken));
};

export const fetchWishList: ReduxFetchAction = () => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { customerSlice } = getState();
        const { customer } = customerSlice;

        if (customer?.id) {
            await dispatch(handleCustomerWishlist());
            return;
        }

        await dispatch(handleGuestWishlist());
    } catch (error) {
        console.error('[fetchWishList]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};
